/**
 * Phase 25 Plan 25-03 — TanStack Query hook for the mention picker.
 *
 * Cache-key shape: ['mention-candidates', projectId, audience, query] —
 * the audience discriminator means an internal-author cache row never
 * resolves an external-author query (and vice-versa) even though the
 * server is the actual gate (CMT-07 / CMT-08).
 *
 * The hook itself is debounce-free; debouncing keystroke → query is the
 * picker component's responsibility (FETCH_DEBOUNCE_MS in MentionPicker).
 */
import { useQuery } from '@tanstack/react-query';
import { fetchMentionCandidates, type MentionCandidate } from '../services/api/mentionCandidates';

export interface UseMentionCandidatesArgs {
  projectId: number | null;
  audience: 'internal' | 'external';
  /** Substring filter; the picker debounces upstream. */
  query: string;
  /** Disable the fetch when the picker is closed. */
  enabled: boolean;
}

export function useMentionCandidates({
  projectId,
  audience,
  query,
  enabled,
}: UseMentionCandidatesArgs) {
  return useQuery<MentionCandidate[]>({
    queryKey: ['mention-candidates', projectId, audience, query],
    queryFn: () => fetchMentionCandidates(projectId as number, query),
    enabled: enabled && projectId !== null && Number.isFinite(projectId),
    staleTime: 5_000,
    // Picker is ephemeral — let stale data surface immediately to keep keystrokes responsive.
    placeholderData: (prev) => prev,
  });
}
